// The one way the console says "working on it", as the tablet does
// (DESIGN.md, Progress indicators): Material 3's flat indeterminate bar along
// an edge. A page loading or saving says so along the top bar's bottom edge;
// a dialog or a sign-in card that is submitting says so along its own top
// edge; a button never grows a spinner, it is disabled meanwhile.
//
// It arrives only after 150ms, so a fast call never flickers, and fades out
// still moving, so a wait ends rather than switching off. It lies over its
// parent, which must be positioned, so nothing shifts as it comes and goes.

import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

const SHOW_DELAY_MS = 150;
// --motion-surface: the fade out takes a surface's time.
const FADE_OUT_MS = 280;

export function TopLoadBar({ active, edge = 'top', label = 'Working' }: { active: boolean; edge?: 'top' | 'bottom'; label?: string }) {
  // hidden → (after the delay) shown → (done) leaving → (faded) hidden.
  const [phase, setPhase] = useState<'hidden' | 'shown' | 'leaving'>('hidden');
  const [prev, setPrev] = useState(active);
  if (active !== prev) {
    setPrev(active);
    if (!active && phase === 'shown') setPhase('leaving');
    else if (active && phase === 'leaving') setPhase('shown');
  }
  useEffect(() => {
    if (!active || phase !== 'hidden') return;
    const t = window.setTimeout(() => setPhase('shown'), SHOW_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [active, phase]);
  useEffect(() => {
    if (phase !== 'leaving') return;
    const t = window.setTimeout(() => setPhase('hidden'), FADE_OUT_MS);
    return () => window.clearTimeout(t);
  }, [phase]);

  if (phase === 'hidden') return null;
  return (
    <div className="top-load-bar" data-edge={edge} data-leaving={phase === 'leaving' || undefined} role="progressbar" aria-label={label} aria-busy="true">
      <span className="top-load-bar-indicator" />
      <span className="top-load-bar-indicator top-load-bar-second" />
    </div>
  );
}

// A dialog's bar: on while any change is on its way, since a dialog is open
// for one change at a time. `active` adds work that is not a change (a code
// being sent, a page's own fetch).
export function DialogLoadBar({ active = false }: { active?: boolean }) {
  const changing = useIsMutating() > 0;
  return <TopLoadBar active={active || changing} />;
}

// The top bar's: while a page's data arrives for the first time (a refetch in
// the background says nothing), and while any change is on its way.
export function PageLoadBar() {
  const firstLoads = useIsFetching({ predicate: (q) => q.state.data === undefined });
  const changing = useIsMutating();
  return <TopLoadBar active={firstLoads + changing > 0} edge="bottom" label="Loading" />;
}
